import React, { useState, useEffect, cloneElement, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactSlider from 'react-slick';

import createStyle from 'shared-utils/src/createStyle';

import Arrows from './components/Arrows';

import usePreventVerticalScroll from './blockVerticalScroll';

import styles, { mainCls } from './style';

const useStyles = createStyle(styles);

const computeToLoad = (x, tot, loaded = [], infinite) => {
  let toLoad;
  if (infinite) {
    toLoad = x === 0
      ? [tot - 1, x, x + 1]
      : [x - 1, x, x + 1];

    if (x - 1 === 0) {      
      toLoad = [...toLoad, tot - 1];
    }

    if (x + 1 === tot - 2) {
      toLoad = [0, ...toLoad];
    }
  } else { 
    toLoad = [x, x + 1, x + 2];
  }
  return [...new Set([...toLoad, ...loaded])];
};

export default function MediaSlider({
  sliderRef,
  initialSlide,
  onChangeIndex,
  keyboardEvts,
  arrows,
  infinite,
  zoom,
  swipe,
  blockVerticalScroll,
  startingImg,
  children,
  fromFloorplanSrp,
}) {
  const sliderId = useRef(Math.random());
  const container = useRef(null);

  const cls = useStyles();

  const [current, setCurrent] = useState(initialSlide);
  const [toLoad, setToLoad] = useState(infinite ? computeToLoad(current, children.length, [], infinite) : [current]);
  const [zoomed, setZoomed] = useState(false);
  const [hidden, setHidden] = useState(true);

  const reveal = () => {
    if (hidden) {
      setTimeout(() => setHidden(false), 125);
    }
  };

  const updateCurrent = (oldIndex, newIndex) => {
    onChangeIndex(newIndex);
    setCurrent(newIndex);
    if (toLoad.length < children.length) {
      setToLoad(computeToLoad(newIndex, children.length, toLoad, infinite));
    }
  };

  const goToPrev = () => sliderRef.current.slickPrev();
  const goToNext = () => sliderRef.current.slickNext();
  const goToIndex = x => sliderRef.current.slickGoTo(x - 1, true);
  
  if (blockVerticalScroll) {
    usePreventVerticalScroll(container);
  }
  
  useEffect(() => {
    const pinchSetting = () => {
      if (window.visualViewport.scale > 1) {
        setZoomed(true);
      } else {
        setZoomed(false);
      }
    };
    
    if (window?.visualViewport?.scale) {
      window.addEventListener(
        'touchend',
        pinchSetting,
        false,
      );
    }

    const handleKeyboardNavigation = (e) => {
      if (e.key === 'ArrowRight') {
        sliderRef.current.slickNext();
      } else if (e.key === 'ArrowLeft') {
        sliderRef.current.slickPrev();
      }
    };

    if (keyboardEvts) {
      window.addEventListener('keydown', handleKeyboardNavigation);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyboardNavigation);  
      window.removeEventListener('touchend', pinchSetting);    
    };
  }, [keyboardEvts, sliderRef]);

  useEffect(() => {
    if (startingImg && parseInt(startingImg, 10) !== initialSlide) {
      goToIndex(startingImg);    
    } else {
      reveal();
    }
  }, [startingImg]);

  return (
    <div
      ref={container}
      className={`${cls[mainCls]}${hidden ? ` ${cls[`${mainCls}--hid`]}` : ''}${zoom ? ` ${cls[`${mainCls}--zoom`]}` : ''} is-rel`}
    >
      <ReactSlider
        ref={sliderRef}
        arrows={false}
        draggable={false}
        infinite={infinite}
        initialSlide={initialSlide}
        beforeChange={updateCurrent}
        afterChange={reveal}
        swipe={swipe && !zoomed}
      >
        {children.map((child, index) => {
          return (
            <div className={`${cls[`${mainCls}__item`]} ${fromFloorplanSrp ? cls[`${mainCls}__item--flrpl`] : ''}`} key={`${sliderId.current}-${index + 1}`}>
            {toLoad.includes(index) && cloneElement(child, {
              current,
              slideIndex: index,
            })}
            </div>
          );
        })}
      </ReactSlider>
      {
        arrows
        && children.length > 1
        && !zoomed
          && (
            <Arrows
              infinite={infinite}
              current={current}
              total={children.length}
              goToPrev={goToPrev}
              goToNext={goToNext}
            />
          )
      }
    </div>
  );
};

MediaSlider.propTypes = {
  sliderRef: PropTypes.instanceOf(Object),
  initialSlide: PropTypes.number,
  infinite: PropTypes.bool,
  onChangeIndex: PropTypes.func,
  keyboardEvts: PropTypes.bool,
  arrows: PropTypes.bool,
  zoom: PropTypes.bool,
  swipe: PropTypes.bool,
  blockVerticalScroll: PropTypes.bool,
  startingImg: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.instanceOf(Object),
    PropTypes.string,
    PropTypes.node,
  ]).isRequired,
  fromFloorplanSrp: PropTypes.bool,
};
MediaSlider.defaultProps = {
  sliderRef: {},
  initialSlide: 0,
  infinite: false,
  onChangeIndex: () => {},
  keyboardEvts: false,
  arrows: false,
  zoom: false,
  swipe: true,
  blockVerticalScroll: false,
  startingImg: null,
  fromFloorplanSrp: false,
};